// RunningIndicator (T-254, running indicator and ETA): "can I deploy yet?" in the header of every interface version, and the banner on /watchers.
//  - Header pill: green IDLE when no long-running job is in flight; amber spinner "RUNNING n" with the time until all are done otherwise.
//    Click opens a list of what is running, how long it has run and how long it should take, from the durations of past runs.
//  - Brief jobs (typical run under 30 s, for example the per-minute workers) are listed but do not turn the pill amber; a job with no
//    history counts as running (the conservative side); an old marker left by a killed script is ignored after its stall cap.
//  - If the last poll failed the pill says UNKNOWN instead of showing an old IDLE.
// Data: GET /api/watchers/running (shared/watcher_api.running_payload). Pure text helpers: lib/watchers.js.
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, CheckCircle2, HelpCircle } from 'lucide-react'
import { useRunning } from '../lib/useRunning'
import { runningHeadline, itemLine, KIND_LABEL } from '../lib/watchers'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const TONE = {
  idle:    { c: '#6ee7b7', b: 'rgba(52,211,153,0.4)',  bg: 'rgba(52,211,153,0.08)' },
  busy:    { c: '#fcd34d', b: 'rgba(245,158,11,0.55)', bg: 'rgba(245,158,11,0.14)' },
  unknown: { c: '#94a3b8', b: 'rgba(148,163,184,0.4)', bg: 'rgba(148,163,184,0.08)' },
}

function Row({ item, fetchedAt, now }) {
  const dim = item.state !== 'RUNNING'
  return (
    <div style={{ padding: '6px 0', borderTop: '1px solid #1e293b', opacity: dim ? 0.6 : 1 }}>
      <div style={{ color: '#e2e8f0', fontSize: '0.78rem' }}>
        {item.name}
        {item.state === 'BRIEF' && <span style={{ ...MONO, fontSize: '0.58rem', color: '#64748b', marginLeft: 6 }}>BRIEF, NOT COUNTED</span>}
        {item.state === 'STALLED' && <span style={{ ...MONO, fontSize: '0.58rem', color: '#fb7185', marginLeft: 6 }}>OLD MARKER, IGNORED</span>}
      </div>
      <div style={{ ...MONO, fontSize: '0.64rem', color: '#94a3b8' }}>
        {item.state === 'STALLED' ? `started ${new Date(item.started_at).toLocaleTimeString()} and never finished: probably killed by a deploy or crash` : itemLine(item, fetchedAt, now)}
      </div>
      <div style={{ ...MONO, fontSize: '0.58rem', color: '#475569' }}>{KIND_LABEL[item.kind] || item.kind} · {item.host}</div>
    </div>
  )
}

export function RunningIndicator() {
  const { data, fetchedAt, now, stale } = useRunning(15_000)
  const [open, setOpen] = useState(false)
  const h = stale && data ? { tone: 'unknown', label: 'UNKNOWN', detail: 'could not refresh: do not trust the last state', allClear: null } : runningHeadline(data, fetchedAt, now)
  const t = TONE[h.tone]
  const Icon = h.tone === 'busy' ? Loader2 : h.tone === 'idle' ? CheckCircle2 : HelpCircle
  const left = h.tone === 'busy' && h.allClear != null ? ` ~${Math.max(1, Math.round(h.allClear / 60))}m` : ''
  return (
    <span style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={`Running jobs: ${h.label}. ${h.detail}`} title={h.detail}
        style={{ ...MONO, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', color: t.c, border: `1px solid ${t.b}`, background: t.bg, borderRadius: 6,
                 padding: '5px 9px', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <Icon className={`h-3.5 w-3.5 ${h.tone === 'busy' ? 'animate-spin' : ''}`} />
        <span>{h.label}</span>
        {left && <span className="hidden sm:inline" style={{ fontWeight: 500 }}>{left}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Running jobs" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 60, width: 'min(360px, 92vw)', background: '#0b1220',
                 border: '1px solid #334155', borderRadius: 8, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ ...MONO, fontSize: '0.7rem', color: t.c, fontWeight: 700 }}>{h.label}</div>
          <div style={{ fontSize: '0.74rem', color: '#cbd5e1', marginBottom: 4 }}>{h.detail}</div>
          {data && data.items.length === 0 && <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Nothing is running.</div>}
          {data && data.items.map(i => <Row key={i.id} item={i} fetchedAt={fetchedAt} now={now} />)}
          <div style={{ ...MONO, fontSize: '0.58rem', color: '#64748b', marginTop: 8, lineHeight: 1.4 }}>
            Covers the registered scripts and backend workers. Not covered: GitHub workflows, Claude routines and anything started by hand.
            The time left is the median of past runs, so it is an estimate.
          </div>
          <Link to="/watchers" onClick={() => setOpen(false)} style={{ ...MONO, fontSize: '0.64rem', color: '#7dd3fc', display: 'inline-block', marginTop: 6 }}>OPEN WATCHERS</Link>
        </div>
      )}
    </span>
  )
}

/** Banner for the top of /watchers: the same facts as the pill, always visible, one row per running job. */
export function RunningBanner() {
  const { data, fetchedAt, now, stale } = useRunning(10_000)
  if (!data) return null
  const h = stale ? { tone: 'unknown', label: 'UNKNOWN', detail: 'could not refresh: do not trust the last state' } : runningHeadline(data, fetchedAt, now)
  const t = TONE[h.tone]
  const shown = data.items.filter(i => i.state !== 'STALLED' || h.tone !== 'idle')
  return (
    <div role="status" style={{ border: `1px solid ${t.b}`, background: t.bg, borderRadius: 6, padding: '8px 12px' }}>
      <div className="flex flex-wrap items-center gap-2">
        {h.tone === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: t.c }} /> : h.tone === 'idle' ? <CheckCircle2 className="h-4 w-4" style={{ color: t.c }} /> : <HelpCircle className="h-4 w-4" style={{ color: t.c }} />}
        <span style={{ ...MONO, fontSize: '0.75rem', fontWeight: 700, color: t.c }}>{h.label}</span>
        <span style={{ fontSize: '0.8rem', color: '#e2e8f0' }}>{h.detail}</span>
      </div>
      {shown.length > 0 && (
        <div style={{ marginTop: 4 }}>{shown.map(i => <Row key={i.id} item={i} fetchedAt={fetchedAt} now={now} />)}</div>
      )}
    </div>
  )
}
